import {
  ActivationType,
  ActivationTypesResponse,
  ActivationTypeResponse,
  CreateActivationTypeRequest,
  UpdateActivationTypeRequest
} from '../types/activation'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class ActivationTypeService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Get all activation types for a customer
   * @param customerId - Customer ID (required)
   * @param activeOnly - Optional filter for only active types
   */
  async getActivationTypes(
    customerId: string,
    activeOnly?: boolean
  ): Promise<ActivationType[]> {
    const url = new URL(`${this.baseURL}/activation-types`)
    url.searchParams.append('customerId', customerId)

    if (activeOnly !== undefined) {
      url.searchParams.append('is_active', String(activeOnly))
    }

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationTypesResponse>(response)

    return responseData.data
  }

  /**
   * Get a single activation type by ID
   * @param id - Activation Type ID
   * @param customerId - Customer ID (required for multi-tenant isolation)
   */
  async getActivationType(id: string, customerId: string): Promise<ActivationType> {
    const url = new URL(`${this.baseURL}/activation-types/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationTypeResponse>(response)

    return responseData.data
  }

  /**
   * Create a new activation type
   * @param customerId - Customer ID (required)
   * @param data - Activation type data
   */
  async createActivationType(
    customerId: string,
    data: CreateActivationTypeRequest
  ): Promise<ActivationType> {
    const url = new URL(`${this.baseURL}/activation-types`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationTypeResponse>(response)

    return responseData.data
  }

  /**
   * Update an existing activation type
   * @param id - Activation Type ID
   * @param customerId - Customer ID (required for multi-tenant isolation)
   * @param data - Update data
   */
  async updateActivationType(
    id: string,
    customerId: string,
    data: UpdateActivationTypeRequest
  ): Promise<ActivationType> {
    const url = new URL(`${this.baseURL}/activation-types/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ActivationTypeResponse>(response)

    return responseData.data
  }

  /**
   * Delete an activation type
   * @param id - Activation Type ID
   * @param customerId - Customer ID (required for multi-tenant isolation)
   */
  async deleteActivationType(id: string, customerId: string): Promise<void> {
    const url = new URL(`${this.baseURL}/activation-types/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    await Promise.race([fetch(url.toString(), config), timeoutPromise])
  }
}

export const activationTypeService = new ActivationTypeService()
